import React from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useDispatch } from "react-redux"
import _ from "lodash"
import { Loader, ToastMessage } from "../../components/index"
import {
    studentLearnAction,
    useStudentLoginMutation,
    useStudentRegisterMutation
} from "../../Redux/actions/lecture.action"
import {
    setStudentSession,
    isStudentAuthenticated
} from "../session"
import { learnPaths } from "../constants"
import LearnHeader from "../layout/LearnHeader"
import { useLazyUseGetAllPlatformsListQuery } from "../../Redux/actions/setting.action"
import useLectureTaxonomy from "../admin/useLectureTaxonomy"

const inputClass =
    "w-full rounded-xl border border-slate-200 px-4 py-3 text-sm shadow-sm outline-none transition focus:border-transparent focus:ring-2 focus:ring-indigo-400"

const labelClass = "mb-1.5 block text-sm font-medium text-slate-700"

/**
 * Sign-in / sign-up for the lecture portal. This is the portal's own auth --
 * it writes the student session only, and never touches the staff session.
 */
export default function StudentAuth({ mode = "login" }) {
    const navigate = useNavigate()
    const location = useLocation()
    const dispatch = useDispatch()

    const [notification, setNotification] = React.useState({
        show: false,
        type: "",
        message: ""
    })

    const [loginAction, loginState] = useStudentLoginMutation()
    const [registerAction, registerState] = useStudentRegisterMutation()

    const isSignup = mode === "signup"
    const activeState = isSignup ? registerState : loginState

    /* Sign-up: pick the school first, then one of that school's classes. */
    const [getInstitutions, institutionsState] =
        useLazyUseGetAllPlatformsListQuery()
    const [institution, setInstitution] = React.useState("")
    const { classes, isLoading: loadingClasses } =
        useLectureTaxonomy(institution)

    React.useEffect(() => {
        if (isSignup) getInstitutions()
    }, [isSignup, getInstitutions])

    const institutions = React.useMemo(() => {
        const list = _.get(institutionsState, "data.list", [])
        return Array.isArray(list) ? list : []
    }, [institutionsState])

    /* Already signed in -- skip straight to the lectures. */
    React.useEffect(() => {
        if (isStudentAuthenticated()) {
            navigate(learnPaths.home, { replace: true })
        }
    }, [navigate, location.pathname])

    /* On success, persist the session and enter the portal. */
    React.useEffect(() => {
        if (!activeState.isSuccess) return

        const token = _.get(activeState, "data.token", null)
        const student = _.get(activeState, "data.student", null)

        if (token && student) {
            setStudentSession({ token, student_data: student })
            /* Never serve another student's cached courses or video links. */
            dispatch(studentLearnAction.util.resetApiState())
            navigate(learnPaths.home, { replace: true })
        }
    }, [activeState, dispatch, navigate])

    React.useEffect(() => {
        if (!activeState.isError) return
        setNotification({
            show: true,
            type: "error",
            message:
                _.get(activeState, "error.data.detail", null) ||
                "Something went wrong. Please try again."
        })
    }, [activeState])

    const handleSubmit = (event) => {
        event.preventDefault()
        const formData = new FormData(event.target)
        const values = Object.fromEntries(formData.entries())

        if (isSignup) {
            /* The school is optional; class and roll number come with it. */
            const required = ["firstname", "lastname", "email", "password"]
            if (values.institution_ref) required.push("class_id", "roll_no")
            if (required.some((field) => !values[field])) {
                setNotification({
                    show: true,
                    type: "info",
                    message: values.institution_ref
                        ? "Please fill in your details, class and roll number."
                        : "Please fill in your name, email and password."
                })
                return
            }
            if (values.password.length < 6) {
                setNotification({
                    show: true,
                    type: "info",
                    message: "Your password must be at least 6 characters."
                })
                return
            }
            registerAction(_.omitBy(values, (value) => value === ""))
            return
        }

        if (!values.email || !values.password) {
            setNotification({
                show: true,
                type: "info",
                message: "Please enter your email and password."
            })
            return
        }
        loginAction({ email: values.email, password: values.password })
    }

    return (
        <div className="flex min-h-screen flex-col bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500">

            {/* The portal's own header, so entering /learn switches context */}
            <LearnHeader minimal />

            <div className="flex flex-1 items-center justify-center px-4 py-8 sm:py-10">
            <div className="w-full max-w-md rounded-3xl bg-white/95 p-6 shadow-2xl backdrop-blur-md sm:max-w-lg sm:p-10">

                <header className="mb-8 text-center">
                    <p className="text-xs font-semibold tracking-widest text-indigo-500">
                        LECTURE PORTAL
                    </p>
                    <h1 className="mt-2 text-2xl font-bold text-slate-800 sm:text-3xl">
                        {isSignup ? "Create your account" : "Welcome back"}
                    </h1>
                    <p className="mt-2 text-sm text-slate-500">
                        {isSignup
                            ? "Sign up to watch your class lectures anytime."
                            : "Sign in to continue watching your class lectures."}
                    </p>
                </header>

                <form className="space-y-5" onSubmit={handleSubmit}>

                    {isSignup && (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label className={labelClass} htmlFor="firstname">
                                    First name
                                </label>
                                <input
                                    className={inputClass}
                                    id="firstname"
                                    name="firstname"
                                    placeholder="Aarav"
                                />
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="lastname">
                                    Last name
                                </label>
                                <input
                                    className={inputClass}
                                    id="lastname"
                                    name="lastname"
                                    placeholder="Sharma"
                                />
                            </div>
                        </div>
                    )}

                    <div>
                        <label className={labelClass} htmlFor="email">
                            Email address
                        </label>
                        <input
                            className={inputClass}
                            type="email"
                            id="email"
                            name="email"
                            placeholder="you@example.com"
                            autoComplete="email"
                        />
                    </div>

                    <div>
                        <label className={labelClass} htmlFor="password">
                            Password
                        </label>
                        <input
                            className={inputClass}
                            type="password"
                            id="password"
                            name="password"
                            placeholder="••••••••"
                            autoComplete={
                                isSignup ? "new-password" : "current-password"
                            }
                        />
                    </div>

                    {isSignup && (
                        <div>
                            <label className={labelClass} htmlFor="institution_ref">
                                School{" "}
                                <span className="font-normal text-slate-400">
                                    (optional)
                                </span>
                            </label>
                            <select
                                className={inputClass}
                                id="institution_ref"
                                name="institution_ref"
                                value={institution}
                                onChange={(event) =>
                                    setInstitution(event.target.value)
                                }
                            >
                                <option value="">
                                    I'm not joining through a school
                                </option>
                                {institutions.map((item) => (
                                    <option key={item.id} value={item.id}>
                                        {item.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Class and roll number only exist within a school. */}
                    {isSignup && institution && (
                        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                            <div>
                                <label className={labelClass} htmlFor="class_id">
                                    Class
                                </label>
                                <select
                                    /* Reset the choice when the school changes. */
                                    key={institution}
                                    className={inputClass}
                                    id="class_id"
                                    name="class_id"
                                    defaultValue=""
                                >
                                    <option value="" disabled>
                                        Select your class
                                    </option>
                                    {classes.map((item) => (
                                        <option
                                            key={item.class_id}
                                            value={item.class_id}
                                        >
                                            {item.class_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelClass} htmlFor="roll_no">
                                    Roll number
                                </label>
                                <input
                                    className={inputClass}
                                    id="roll_no"
                                    name="roll_no"
                                    placeholder="06"
                                />
                            </div>
                        </div>
                    )}

                    <button
                        type="submit"
                        className="w-full rounded-xl bg-indigo-600 py-3 font-semibold text-white shadow-lg transition-all hover:bg-indigo-700 disabled:opacity-60"
                        disabled={activeState.isLoading}
                    >
                        {isSignup ? "Create account" : "Sign in"}
                    </button>
                </form>

                <p className="mt-6 text-center text-sm text-slate-500">
                    {isSignup
                        ? "Already have an account? "
                        : "New to the portal? "}
                    <button
                        type="button"
                        className="font-semibold text-indigo-600 hover:underline"
                        onClick={() =>
                            navigate(
                                isSignup ? learnPaths.login : learnPaths.signup
                            )
                        }
                    >
                        {isSignup ? "Sign in" : "Create an account"}
                    </button>
                </p>
            </div>
            </div>

            {(activeState.isLoading ||
                institutionsState.isFetching ||
                loadingClasses) && <Loader show={true} />}

            {notification.show && (
                <ToastMessage
                    {...notification}
                    onClose={() =>
                        setNotification({ show: false, type: "", message: "" })
                    }
                />
            )}
        </div>
    )
}
